/**
 * LoginScreen — Cognito-backed gate for early access.
 *
 * Login-only (no self signup). If Cognito returns a NEW_PASSWORD_REQUIRED
 * challenge (typical for admin-created users on first login) the screen
 * swaps the form to ask for a new password.
 */
import React, { useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useAuth } from '../lib/auth';
import { colors } from '../lib/theme';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginScreen() {
  const { signIn, completeNewPassword } = useAuth();
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [newPassword, setNewPassword]   = useState('');
  const [busy, setBusy]                 = useState(false);
  const [error, setError]               = useState(null);
  // 'credentials' | 'newPassword'
  const [step, setStep]                 = useState('credentials');

  async function handleSubmit() {
    if (busy) return;
    setError(null);

    if (step === 'credentials') {
      const normalized = email.trim().toLowerCase();
      if (!normalized || !password) {
        setError('Enter your email and password.');
        return;
      }
      if (!EMAIL_RE.test(normalized)) {
        setError('Enter a valid email address.');
        return;
      }
      setBusy(true);
      const res = await signIn(normalized, password);
      setBusy(false);
      if (res.ok) return;
      if (res.nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setStep('newPassword');
        setError(null);
        return;
      }
      setError(res.message);
      return;
    }

    // newPassword step
    if (!newPassword || newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    const res = await completeNewPassword(newPassword);
    setBusy(false);
    if (!res.ok) setError(res.message);
  }

  const isNewPasswordStep = step === 'newPassword';

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.appBackground }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
          keyboardShouldPersistTaps="handled"
        >
          <View className="px-6">
            <View className="items-center mb-8">
              <View className="px-4 py-1.5 rounded-full border border-sky/20 bg-sky/10 mb-4">
                <Text className="text-[11px] font-bold uppercase tracking-widest text-sky">
                  GarageIQ
                </Text>
              </View>
              <Text className="text-3xl font-black text-white text-center">
                early access login
              </Text>
              <Text className="text-sm text-white/50 text-center mt-2">
                {isNewPasswordStep
                  ? 'Choose a new password to finish signing in.'
                  : 'Sign in with the credentials provided to your early access account.'}
              </Text>
            </View>

            <View className="bg-charcoal/50 border border-white/10 rounded-2xl p-5">
              {!isNewPasswordStep && (
                <>
                  <Text className="text-xs font-bold uppercase tracking-widest text-white/60 mb-1.5">
                    Email
                  </Text>
                  <TextInput
                    value={email}
                    onChangeText={setEmail}
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="email"
                    keyboardType="email-address"
                    textContentType="emailAddress"
                    editable={!busy}
                    placeholder="you@example.com"
                    placeholderTextColor="rgba(255,255,255,0.35)"
                    style={inputStyle}
                  />

                  <Text className="text-xs font-bold uppercase tracking-widest text-white/60 mb-1.5 mt-4">
                    Password
                  </Text>
                  <TextInput
                    value={password}
                    onChangeText={setPassword}
                    secureTextEntry
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="password"
                    textContentType="password"
                    editable={!busy}
                    placeholder="••••••••"
                    placeholderTextColor="rgba(255,255,255,0.35)"
                    style={inputStyle}
                  />
                </>
              )}

              {isNewPasswordStep && (
                <>
                  <Text className="text-xs font-bold uppercase tracking-widest text-white/60 mb-1.5">
                    New password
                  </Text>
                  <TextInput
                    value={newPassword}
                    onChangeText={setNewPassword}
                    secureTextEntry
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="new-password"
                    textContentType="newPassword"
                    editable={!busy}
                    placeholder="At least 8 characters"
                    placeholderTextColor="rgba(255,255,255,0.35)"
                    style={inputStyle}
                  />
                </>
              )}

              {error && (
                <View className="mt-4 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10">
                  <Text className="text-sm text-red-300">{error}</Text>
                </View>
              )}

              <Pressable
                onPress={handleSubmit}
                disabled={busy}
                style={({ pressed }) => ({
                  marginTop: 20,
                  backgroundColor: busy ? colors.btnPrimaryHover : colors.btnPrimary,
                  opacity: pressed && !busy ? 0.85 : 1,
                  paddingVertical: 14,
                  borderRadius: 12,
                  alignItems: 'center',
                })}
              >
                {busy ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text className="text-base font-bold text-white">
                    {isNewPasswordStep ? 'Set password & continue' : 'Sign in'}
                  </Text>
                )}
              </Pressable>
            </View>

            <Text className="text-xs text-white/40 text-center mt-6">
              Access is limited to invited testers.
            </Text>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const inputStyle = {
  backgroundColor: 'rgba(255,255,255,0.06)',
  borderWidth: 1,
  borderColor: 'rgba(255,255,255,0.12)',
  borderRadius: 10,
  paddingHorizontal: 14,
  paddingVertical: 12,
  color: '#fff',
  fontSize: 16,
};
